import {
  AfterViewInit,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  ViewChild,
  ViewContainerRef,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Router } from '@angular/router';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { faWandMagic } from '@fortawesome/free-solid-svg-icons';

import { firstValueFrom } from 'rxjs';

import { UserWorkspacesService } from 'src/app/core/services/user-workspaces.service';
import { WorkspaceObjectsService } from 'src/app/core/services/workspace-objects.service';

import { ToastService } from '@services/toast.service';
import { LoggerService } from '@services/logger.service';
import { DialogCloseReason, DialogCloseOptions } from '@sui/dialog';

import { TabDialogBase } from 'src/app/shared/abstract/tab-dialog-base';
import { ButtonConfig } from 'src/app/admin/models';

import { UseCaseApiService } from 'src/app/api/services/use-case-api.service';
import { injectUseCaseTemplateSchemaQuery } from 'src/app/api/queries/use-case.queries';
import {
  UseCaseProvisionFailure,
  UseCaseProvisionSuccess,
  UseCaseTemplateSchema,
  UseCaseTemplateSummary,
  UseCaseWorkspaceChoice,
} from 'src/app/core/models/use-case.models';

import { UseCaseTemplateSelectorComponent } from './steps/use-case-template-selector/use-case-template-selector.component';
import { UseCaseInputsStepComponent } from './steps/use-case-inputs-step/use-case-inputs-step.component';
import { UseCasePreviewStepComponent } from './steps/use-case-preview-step/use-case-preview-step.component';

interface DialogData {}
interface DialogOutput {}

const STEP_TEMPLATE = 0;
const STEP_BUSINESS = 1;
const STEP_PREFERENCES = 2;
const STEP_PREVIEW = 3;

/**
 * "Create Use Case" wizard (SPEC §12). Collects business inputs against a
 * template, previews the server-computed plan, and fires a single provision
 * call (§1.4 - no client-side chaining). Extends TabDialogBase; steps are
 * dynamic tab components managed by the tab manager.
 *
 * ADR 0005: this iteration ships WITHOUT the permissions/sharing sub-step
 * (provisioned objects are owner-only). The step would slot in between
 * Preferences and Preview when the ObjectPermissionsFacade fix lands.
 */
@Component({
  selector: 'app-use-case-wizard-dialog',
  templateUrl: './use-case-wizard-dialog.component.html',
  styleUrls: ['./use-case-wizard-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UseCaseWizardDialogComponent
  extends TabDialogBase<DialogData, DialogOutput>
  implements OnInit, AfterViewInit
{
  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  protected destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly useCaseApi = inject(UseCaseApiService);
  private readonly queryClient = inject(QueryClient);
  private readonly router = inject(Router);
  private readonly userWorkspaces = inject(UserWorkspacesService);
  private readonly workspaceObjects = inject(WorkspaceObjectsService);
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService);

  readonly selectedTemplateId = signal<string>('');
  private schemaQuery = injectUseCaseTemplateSchemaQuery(
    this.selectedTemplateId,
  );

  currentStep = STEP_TEMPLATE;
  objectIcon = faWandMagic;
  objectNameTitle = '';
  wizardSteps: string[] = [];
  private isProvisioning = false;

  protected buttons: ButtonConfig[] = [
    {
      variant: 'primary',
      text: this.translateService.instant('use-case-wizard.button.previous'),
      leadingIcon: ['fas', 'arrow-left'],
      action: () => this.previousStep(),
      disabled: true,
    },
    {
      variant: 'primary',
      text: this.translateService.instant('use-case-wizard.button.next'),
      leadingIcon: ['fas', 'arrow-right'],
      action: () => this.nextStep(),
      disabled: true,
    },
  ];

  ngOnInit(): void {
    this.objectNameTitle = this.translateService.instant(
      'use-case-wizard.title.create-use-case',
    );
    this.wizardSteps = [
      this.translateService.instant('use-case-wizard.step.choose-template'),
      this.translateService.instant('use-case-wizard.step.business-inputs'),
      this.translateService.instant('use-case-wizard.step.preferences'),
      this.translateService.instant('use-case-wizard.step.preview-and-confirm'),
    ];
    this.setupTabs();

    // When the schema for the chosen template arrives, rebuild the input steps
    // (pattern: the feed wizard's handleFeedTypeChange + reloadTab).
    // untracked: reloadTab reads AND writes the tab manager's formGroups signal
    // (register/unregister on component create/destroy); tracking it here makes
    // the effect re-trigger itself forever and freezes the app.
    effect(
      () => {
        const schema = this.schemaQuery.data();
        if (schema) {
          untracked(() => this.applySchema(schema));
        }
      },
      { injector: this.injector },
    );
  }

  ngAfterViewInit(): void {
    this.initializeBase();

    const selector =
      this.tabManager.getTabComponent<UseCaseTemplateSelectorComponent>(
        'template',
      );
    if (selector?.templateSelected) {
      selector.templateSelected
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((template: UseCaseTemplateSummary) =>
          this.handleTemplateSelected(template),
        );
    }
  }

  /**
   * Declare the wizard steps. Input steps start with empty fields; they are
   * reloaded once the template schema loads.
   *
   * @return void
   */
  private setupTabs(): void {
    this.tabs = [
      {
        id: 'template',
        label: this.translateService.instant(
          'use-case-wizard.step.choose-template',
        ),
        component: UseCaseTemplateSelectorComponent,
        inputs: {},
      },
      {
        id: 'business',
        label: this.translateService.instant(
          'use-case-wizard.step.business-inputs',
        ),
        component: UseCaseInputsStepComponent,
        inputs: { fields: [], group: 'business' },
      },
      {
        id: 'preferences',
        label: this.translateService.instant(
          'use-case-wizard.step.preferences',
        ),
        component: UseCaseInputsStepComponent,
        inputs: { fields: [], group: 'preferences' },
      },
      // ADR 0005: a Permissions/sharing step would be declared here (reusing
      // ObjectPermissionsComponent) once the ObjectPermissionsFacade fix lands.
      // Deferred this iteration - provisioned objects are owner-only.
      {
        id: 'preview',
        label: this.translateService.instant(
          'use-case-wizard.step.preview-and-confirm',
        ),
        component: UseCasePreviewStepComponent,
        inputs: {},
        excludeFromValues: true,
      },
    ];
  }

  /**
   * Feed the loaded schema into the two input steps and rebuild them. The
   * preferences step also receives the template's default workspace mode (ADR
   * 0006, §3.1) for its fixed Workspace sub-section.
   *
   * @param schema the loaded template schema
   * @return void
   */
  private applySchema(schema: UseCaseTemplateSchema): void {
    const fields = schema.inputs;
    this.tabs[STEP_BUSINESS].inputs = { fields, group: 'business' };
    this.tabs[STEP_PREFERENCES].inputs = {
      fields,
      group: 'preferences',
      workspaceMode: schema.default_workspace_mode ?? 'new',
    };
    this.tabManager.reloadTab('business');
    this.tabManager.reloadTab('preferences');
  }

  /**
   * Store the chosen template (triggering the schema query) and enable Next.
   *
   * @param template the chosen template
   * @return void
   */
  private handleTemplateSelected(template: UseCaseTemplateSummary): void {
    this.selectedTemplateId.set(template.id);
    this.updateNavigationButtons();
  }

  /**
   * Advance one step, running preview when entering the preview step and
   * provisioning when confirming from it.
   *
   * @return void
   */
  nextStep(): void {
    if (this.currentStep === STEP_PREVIEW) {
      this.provision();
      return;
    }

    const currentFormGroup = this.tabManager.getTabFormGroup(
      this.tabs[this.currentStep].id,
    );
    if (currentFormGroup) {
      this.tabManager.markActiveFormGroupAsTouched(currentFormGroup);
      if (
        currentFormGroup.status !== 'VALID' &&
        currentFormGroup.status !== 'DISABLED'
      ) {
        return;
      }
    }

    this.currentStep = this.currentStep + 1;
    this.tabManager.selectTab(this.tabs[this.currentStep].id);
    if (this.currentStep === STEP_PREVIEW) {
      this.runPreview();
    }
    this.updateNavigationButtons();
  }

  /**
   * Step back one step.
   *
   * @return void
   */
  previousStep(): void {
    if (this.currentStep === STEP_TEMPLATE) {
      return;
    }
    this.currentStep = this.currentStep - 1;
    this.tabManager.selectTab(this.tabs[this.currentStep].id);
    this.updateNavigationButtons();
  }

  /**
   * Refresh the nav buttons for the current step: disable Next on the template
   * step until a template is chosen, and turn Next into Create on the preview
   * step gated on preview validity.
   *
   * @return void
   */
  private updateNavigationButtons(): void {
    const isFirst = this.currentStep === STEP_TEMPLATE;
    const isPreview = this.currentStep === STEP_PREVIEW;

    this.buttons[0].disabled = isFirst;

    if (isFirst) {
      this.buttons[1].disabled = this.selectedTemplateId() === '';
    } else if (isPreview) {
      const previewGroup = this.tabManager.getTabFormGroup('preview');
      this.buttons[1].disabled = previewGroup?.valid !== true;
    } else {
      this.buttons[1].disabled = false;
    }

    if (isPreview) {
      this.buttons[1].text = this.translateService.instant(
        'use-case-wizard.button.create',
      );
      this.buttons[1].leadingIcon = ['fas', 'check'];
      this.buttons[1].variant = 'primary';
    } else {
      this.buttons[1].text = this.translateService.instant(
        'use-case-wizard.button.next',
      );
      this.buttons[1].leadingIcon = ['fas', 'arrow-right'];
      this.buttons[1].variant = 'primary';
    }

    this.cdr.markForCheck();
  }

  /**
   * Run the preview call for the current inputs and push the result into the
   * preview step (§9.2). Re-enables Create when the plan is valid.
   *
   * @return void
   */
  private async runPreview(): Promise<void> {
    const preview =
      this.tabManager.getTabComponent<UseCasePreviewStepComponent>('preview');
    preview?.setLoading();
    this.updateNavigationButtons();

    try {
      const response = await firstValueFrom(
        this.useCaseApi.preview({
          templateId: this.selectedTemplateId(),
          inputs: this.collectInputs(),
          workspace: this.buildWorkspace(),
        }),
      );
      preview?.setPreview(response);
      this.prefillResolvedWorkspaceTitle(response);
    } catch (error) {
      this.logger.error('Use case preview failed', error);
      preview?.setError();
    }

    this.updateNavigationButtons();
  }

  /**
   * After preview, prefill the preferences step's new-workspace title from the
   * planner-resolved plan.workspace.title (§9.2), so the user sees (and can
   * edit) the name that will be created for mode=new.
   *
   * @param response the preview response
   * @return void
   */
  private prefillResolvedWorkspaceTitle(response: {
    plan?: { workspace?: { mode: string; title: string } | null };
  }): void {
    const workspace = response.plan?.workspace;
    if (workspace && workspace.mode === 'new') {
      this.tabManager
        .getTabComponent<UseCaseInputsStepComponent>('preferences')
        ?.applyResolvedWorkspaceTitle(workspace.title);
    }
  }

  /**
   * Fire the single provision call (§1.4). On success: invalidate use_case
   * queries, close, and navigate to the created dashboard. On a §9.4 failure or
   * pre-write validation failure: keep the wizard open and surface the reason.
   *
   * @return void
   */
  private async provision(): Promise<void> {
    if (this.isProvisioning) {
      return;
    }
    this.isProvisioning = true;
    this.buttons[1].loading = true;
    this.buttons[1].disabled = true;
    this.cdr.markForCheck();

    const preview =
      this.tabManager.getTabComponent<UseCasePreviewStepComponent>('preview');

    try {
      const response = await firstValueFrom(
        this.useCaseApi.provision({
          template_id: this.selectedTemplateId(),
          inputs: this.collectInputs(),
          workspace: this.buildWorkspace(),
        }),
      );

      if (response.success) {
        await this.onProvisionSuccess(response);
      } else {
        // Pre-write validation failed - re-run preview to show the fresh errors.
        this.resetConfirmButton();
        await this.runPreview();
      }
    } catch (error) {
      const failure = this.extractFailure(error);
      if (failure) {
        preview?.setProvisionFailure(failure);
      } else {
        this.toastService.showError(
          this.translateService.instant(
            'use-case-wizard.toast.provisioning-failed',
          ),
        );
      }
      this.resetConfirmButton();
    } finally {
      this.isProvisioning = false;
    }
  }

  /**
   * Handle a successful provision: invalidate affected queries, toast, close,
   * and navigate to the first created dashboard.
   *
   * @param response the provision success envelope
   * @return void
   */
  private async onProvisionSuccess(
    response: UseCaseProvisionSuccess,
  ): Promise<void> {
    // Invalidate the use_case query trail (templates + instances).
    await this.queryClient.invalidateQueries({ queryKey: ['use_case'] });

    // The workspace screen refetches when it mounts, but a cached entry for an
    // existing workspace would serve the widget sidebar a list without the new
    // feeds - so drop both before navigating.
    this.refreshUserWorkspaces();

    this.toastService.showSuccess(
      this.translateService.instant('use-case-wizard.toast.use-case-created'),
    );

    this.dirtyHandler.closeImmediately({
      reason: DialogCloseReason.Save,
      data: {} as DialogOutput,
    });

    this.navigateToDashboard(response);
  }

  /**
   * Drop the cached workspace list (so a newly provisioned workspace appears in
   * the switcher/list) and every cached workspace-object list. Fire-and-forget:
   * the home screen's query refetches when it observes the invalidation, so
   * navigation is not blocked on it.
   *
   * @return void
   */
  private refreshUserWorkspaces(): void {
    this.userWorkspaces.invalidate();
    this.workspaceObjects.invalidate();
  }

  /**
   * Navigate to the first created dashboard from the provision envelope, if one
   * carries a workspace id.
   *
   * @param response the provision success envelope
   * @return void
   */
  private navigateToDashboard(response: UseCaseProvisionSuccess): void {
    const dashboard = response.object?.dashboards?.[0];
    if (dashboard?.id && dashboard.workspace_id) {
      this.router.navigate([
        '/analytics/workspace',
        dashboard.workspace_id,
        'dashboard',
        dashboard.id,
      ]);
    }
  }

  /**
   * Assemble the provision inputs from the tab manager, dropping the wizard's
   * own control keys (template_id, preview gate) and the fixed Workspace
   * sub-section controls (ADR 0006) - those travel in the §9.5 workspace object
   * built by buildWorkspace(), not in `inputs`.
   *
   * @returns the inputs map
   */
  private collectInputs(): Record<string, unknown> {
    const inputs = { ...this.tabManager.combineValues() };
    delete inputs['template_id'];
    delete inputs['preview_valid'];
    delete inputs['workspace_mode'];
    delete inputs['workspace_title'];
    delete inputs['workspace_id'];

    return inputs;
  }

  /**
   * Build the §9.5 workspace choice from the preferences step's fixed Workspace
   * sub-section (ADR 0006). Preview and provision send the identical object.
   *
   * @returns the workspace choice
   */
  private buildWorkspace(): UseCaseWorkspaceChoice {
    const preferences =
      this.tabManager.getTabComponent<UseCaseInputsStepComponent>(
        'preferences',
      );

    return preferences?.getWorkspaceChoice() ?? { mode: 'new', title: '' };
  }

  /**
   * Pull the §9.4 failure body out of an HTTP 422 error, if present.
   *
   * @param error the caught error
   * @returns the failure body or null
   */
  private extractFailure(error: unknown): UseCaseProvisionFailure | null {
    const body = (error as { error?: unknown })?.error ?? error;
    const candidate = body as Partial<UseCaseProvisionFailure> | undefined;

    return candidate && candidate.success === false && 'failed_ref' in candidate
      ? (candidate as UseCaseProvisionFailure)
      : null;
  }

  /**
   * Restore the confirm button after a failed provision attempt.
   *
   * @return void
   */
  private resetConfirmButton(): void {
    this.buttons[1].loading = false;
    this.updateNavigationButtons();
  }

  /**
   * Close the wizard, honouring dirty-state protection.
   *
   * @return void
   */
  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({ reason: DialogCloseReason.Cancel });
  }

  protected override performSave(
    formData: unknown,
  ): DialogCloseOptions<DialogOutput> {
    this.provision();

    return { reason: DialogCloseReason.Save, data: formData as DialogOutput };
  }
}
